<template>
    <section class="vh-auto">
        <div v-if="mission != null" class="container h-100">
            <div class="row d-flex justify-content-center align-items-center h-100">
                <div class="col col-lg-12 mb-4 mb-lg-0">
                    <div class="card mb-3 shadow p-3 mb-5 bg-body rounded" style="border-radius: .5rem;">
                        <div class="card-body p-4">
                            <div class="row align-items-center mb-2">
                                <div class="col">
                                    <h4 v-if="mission.status === 'DRAFT' || mission.status === 'PENDING'"
                                        class="mb-0 text-truncate">Démarrer la mission</h4>
                                    <h4 v-if="mission.status === 'ONGOING'" class="mb-0 text-truncate">Terminer la
                                        mission</h4>
                                </div>
                                <div class="col-auto" v-if="tutorialHref">
                                    <a :href="tutorialHref" target="_blank" rel="noopener noreferrer"
                                        class="btn btn-outline-secondary btn-sm">
                                        <font-awesome-icon :icon="['fas', 'circle-info']" class="me-1" />
                                        Aide
                                    </a>
                                </div>
                            </div>

                            <!-- notification section (pilotée par le parent) -->
                            <template v-if="notification">
                                <hr class="mt-2">
                                <div class="row">
                                    <div class="col-12">
                                        <div ref="notif" class="alert"
                                            :class="[notification.alertClass, { 'notify-shake': notifShake }]"
                                            v-html="notification.html">
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- Drivers verif -->
                            <hr class="mb-5 mt-0">
                            <div class="row d-flex justify-content-between mb-4">
                                <div class="col-12 col-md-6 mx-auto">
                                    <MembersVerify :mission="mission" @refresh="$emit('refresh')"
                                        @setAlertCode="$emit('setAlertCode', $event)" />
                                </div>
                            </div>

                            <!-- Convoy tests -->
                            <template v-if="mission.status === 'PENDING'">
                                <hr class="mb-5 mt-0">
                                <div class="row pt-1 mb-5 d-flex justify-content-center"
                                    :class="{ 'locked-section': lockConvoy }">
                                    <div class="col-12">
                                        <ConvoyPrechecks :mission="mission" @refresh="$emit('refresh')" />
                                    </div>
                                </div>
                            </template>

                            <!-- Commentaire -->
                            <hr class="mb-5">
                            <div class="row d-flex justify-content-center mb-4"
                                :class="{ 'locked-section': lockCommentPicker }">
                                <div class="col-6">
                                    <div>
                                        <MissionCommentPicker :mission="mission"
                                            :disabled="$isViewer || lockCommentPicker"
                                            @startCommentChange="onStartCommentChangeLocal"
                                            @endCommentChange="$emit('endCommentChange', $event)" />
                                    </div>
                                </div>
                            </div>

                            <!-- Démarrer button -->
                            <template v-if="mission.status == 'PENDING' || mission.status == 'ONGOING'">
                                <div class="row pt-1 mb-4" :class="{ 'locked-section': lockStartBlock }">
                                    <div v-if="mission.status == 'PENDING' && !$isViewer" class="col-12 text-center">
                                        <ButtonDisplay @click="this.startMission()" :type="'start'" text="Démarrer"
                                            :disabled="lockStartBlock" />
                                    </div>
                                    <div v-else-if="mission.status == 'ONGOING' && !$isViewer"
                                        class="col-12 text-center">
                                        <ButtonDisplay @click="this.endMission()" :type="'stop'" text="Terminer" />
                                    </div>
                                </div>
                            </template>

                            <!-- Comments -->
                            <hr class="mt-0 mb-3">
                            <div class="row pt-1 mb-5">
                                <div class="col-6 small">
                                    <CommentsList v-if="mission" ref="commentsList" :missionId="mission.id" />
                                </div>
                                <div class="col-6 small">
                                    <CommentAdd :disabled="!(this.$isOperator || this.$isAdmin)"
                                        @submit="onSubmitComment" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <!-- Modal de re-vérification chauffeur (2/2) -->
    <template v-if="showVerifyModal">
        <div class="modal fade show" style="display:block; background: rgba(0,0,0,.5);">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Re-vérification du chauffeur</h5>
                        <button type="button" class="btn-close" aria-label="Close"
                            @click="showVerifyModal = false"></button>
                    </div>
                    <div class="modal-body">
                        <!-- On réutilise le composant existant -->
                        <MembersVerify :mission="mission" :forceShowInputs="true" @refresh="onVerifyRefresh"
                            @setAlertCode="$emit('setAlertCode', $event)" />
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-outline-secondary"
                            @click="showVerifyModal = false">Annuler</button>
                    </div>
                </div>
            </div>
        </div>
    </template>
</template>

<script>
import ButtonDisplay from '@/components/display/ButtonDisplay.vue'
import CommentAdd from '../../Components/CommentAdd.vue'
import CommentsList from '../../Components/CommentsList.vue'
import MissionCommentPicker from '../../Components/MissionCommentPicker.vue'
import MembersVerify from '../../Components/Members/MembersVerify.vue'
import ConvoyPrechecks from '../../Components/Convoy/ConvoyPrechecks.vue'


export default {
    props: ["mission", "notification"],
    components: {
        ButtonDisplay,
        CommentAdd,
        CommentsList,
        MissionCommentPicker,
        MembersVerify,
        ConvoyPrechecks
    },
    data() {
        return { notifShake: false, showVerifyModal: false, startCommentDraft: '' };
    },
    computed: {
        // 📘 Lien Tutoriel selon le statut (adapte les steps si besoin)
        tutorialHref() {
            const status = this.mission?.status;
            const map = {
                DRAFT: '/tutorial/mission/12',   // ex. préparation
                PENDING: '/tutorial/mission/12', // ex. démarrage
                ONGOING: '/tutorial/mission/23', // ex. fin de mission
            };
            return map[status] || '';
        },
        // ✅ État de vérification chauffeur via verifiedInfo
        isDriverVerifiedTwice() {
            return this.mission?.verifiedInfo === 'TWICE';
        },
        isDriverVerifiedOnce() {
            return this.mission?.verifiedInfo === 'ONCE';
        },
        // compat : utilisé ailleurs dans ce composant
        isDriverVerified() {
            return this.mission?.driverVerified === true;
        },
        // ✅ Tous les pré-checks du convoi validés ?
        allPrechecksOk() {
            const vals = Array.isArray(this.mission?.missionPrecheckValues) ? this.mission.missionPrecheckValues : [];
            // S'il n'y a pas de pré-checks, on considère "OK"
            if (vals.length === 0) return true;
            return vals.every(v => v?.value === true);
        },
        // 🔒 Verrous d’interface
        // 1) Tant que chauffeur pas doublement vérifié → verrou sur convoi + commentaire + bouton
        lockConvoy() {
            return !this.isDriverVerified;
        },
        // 2) Une fois chauffeur validé, si pré-checks pas tous OK → verrou sur commentaire + bouton
        lockCommentPicker() {
            return !this.isDriverVerified;
        },
        lockStartBlock() {
            return !this.isDriverVerified;
        }
    },
    methods: {
        onStartCommentChangeLocal(value) {
            this.startCommentDraft = (value || '').trim();
            this.$emit('startCommentChange', value);
        },
        onSubmitComment(content) {
            this.$emit("addComment", content);
        },
        reloadComments() {
            this.$refs.commentsList?.reload();
        },
        startMission() {
            // Si 1ère vérification déjà faite (ONCE), modale UNIQUEMENT si commentaire de départ présent
            if (this.mission?.status === 'PENDING' && this.isDriverVerifiedOnce) {
                if (!this.startCommentDraft) { this.focusNotification(); return; }
                this.showVerifyModal = true;
                return;
            }
            if (!(this.mission?.transporteurId || (this.mission?.transporteur && this.mission.transporteur.id))) {
                this.focusNotification();
                //alert("Impossible de démarrer : aucun véhicule principal n’est assigné à cette mission.");
                return;
            }
            this.$emit("startMission", this.mission.id);
        },
        // callback quand la vérif 2/2 réussit dans la popin
        onVerifyRefresh() {
            this.showVerifyModal = false;
            this.$emit('startMission', this.mission.id);
        },
        focusNotification() {
            // scroll vers le bandeau puis petite vibration
            const el = this.$refs.notif;
            try {
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch (e) {
                /* ignore: certains environnements peuvent ne pas supporter scrollIntoView smooth */
            }
            // retrigger l’animation
            this.notifShake = false;
            this.$nextTick(() => {
                this.notifShake = true;
                setTimeout(() => (this.notifShake = false), 600);
            });
        },
        endMission() {
            const ok = confirm("Avez-vous bien vérifié le mot de passe du chauffeur ?");
            if (!ok) return;
            this.$emit("endMission", this.mission.id);
        },
    }
}
</script>

<style scoped>
.locked-section {
    position: relative;
    filter: grayscale(0.8);
    opacity: 0.2;
    pointer-events: none;
}

.locked-section::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.35);
    border-radius: .5rem;
}

/* Animation de mise en avant du bandeau */
@keyframes notif-shake {

    10%,
    90% {
        transform: translateX(-1px);
    }

    20%,
    80% {
        transform: translateX(2px);
    }

    30%,
    50%,
    70% {
        transform: translateX(-4px);
    }

    40%,
    60% {
        transform: translateX(4px);
    }
}

.notify-shake {
    animation: notif-shake 0.45s ease;
    box-shadow: 0 0 0 .2rem rgba(255, 193, 7, .25);
    /* léger halo (jaune bootstrap) */
}
</style>